import React from 'react';
import i18next from 'i18next';
import { css } from '@emotion/core';
import PropTypes from 'prop-types';
import FeverButton from 'components/common/Button/FeverButton';
import Backdrop from '@material-ui/core/Backdrop';
import Fade from '@material-ui/core/Fade';
import IconButton from '@material-ui/core/IconButton';
import Modal from '@material-ui/core/Modal';
import Typography from '@material-ui/core/Typography';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CloseIcon from '@material-ui/icons/Close';
import { white, functional, success, system900 } from 'Mui/styles/color';
import { dp03 } from 'Mui/styles/shadow';
import { stylePropType, childrenPropType } from 'utils/propTypes';
import useScrollElementOnMount from 'client/hooks/useScrollElementOnMount';

const PureDialog = ({
    children,
    open,
    isShowHeaderIcon = false,
    isShowCloseIcon = true,
    isShowPrimaryButton = true,
    isShowSecondaryButton = false,
    isPrimaryButtonDisabled = false,
    isSecondaryButtonDisabled = false,
    isLoading = false,
    titleAlign = 'left',
    contentAlgin = 'left',
    titleText,
    contentText,
    primaryButtonText = i18next.t('btn.confirmed'),
    secondaryButtonText = i18next.t('btn.cancel'),
    iconStyle,
    buttonStyle,
    modalStyle,
    dialogContentStyle,
    Icon = CheckCircleIcon,
    onPrimaryButtonClick = () => {},
    onSecondaryButtonClick = () => {},
    onClose = () => {}
}) => {
    const hasHeader = Boolean(isShowHeaderIcon) && Boolean(titleText) && Boolean(isShowCloseIcon);
    const bindRef = useScrollElementOnMount();
    return (
        <Modal
            open={open}
            onClose={onClose}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{
                timeout: 500
            }}
            aria-labelledby={titleText}
            aria-describedby={contentText}
            css={[modalContainerStyle, modalStyle]}
        >
            <Fade in={open} css={css(`outline: none`)}>
                <div css={containerStyle} ref={bindRef}>
                    <div css={headerStyle}>
                        <div css={headerInfoStyle}>
                            {Boolean(isShowHeaderIcon) && Icon && typeof Icon === 'object' && (
                                <div css={iconWrapperStyle(iconStyle)}>
                                    <Icon />
                                </div>
                            )}
                            {titleText && (
                                <div css={titleWrapperStyle(titleAlign, isShowCloseIcon)}>
                                    <Typography variant="h6">{titleText}</Typography>
                                </div>
                            )}
                        </div>
                        {Boolean(isShowCloseIcon) && (
                            <div css={closeIconWrapperStyle}>
                                <IconButton css={closeIconStyle} size="small" onClick={onClose}>
                                    <CloseIcon />
                                </IconButton>
                            </div>
                        )}
                    </div>
                    <div css={[contentStyle(hasHeader), dialogContentStyle]}>
                        {children ? (
                            children
                        ) : Boolean(contentText) ? (
                            <Typography variant="subtitle2" css={contentTextStyle(contentAlgin)}>
                                {contentText}
                            </Typography>
                        ) : null}
                    </div>
                    {(Boolean(isShowPrimaryButton) || Boolean(isShowSecondaryButton)) && (
                        <div css={footerStyle}>
                            {Boolean(isShowPrimaryButton) && (
                                <FeverButton
                                    text={primaryButtonText}
                                    loading={isLoading}
                                    disabled={isPrimaryButtonDisabled}
                                    customBtnStyle={css([primaryButtonStyle, buttonStyle])}
                                    componentStyle={{
                                        textColor: white.color,
                                        bgColor: functional.color
                                    }}
                                    onClick={onPrimaryButtonClick}
                                />
                            )}
                            {Boolean(isShowSecondaryButton) && (
                                <FeverButton
                                    text={secondaryButtonText}
                                    loading={false}
                                    disabled={isSecondaryButtonDisabled}
                                    customBtnStyle={css([secondaryButtonStyle, buttonStyle])}
                                    componentStyle={{
                                        textColor: system900.color,
                                        bgColor: 'transparent'
                                    }}
                                    onClick={onSecondaryButtonClick}
                                />
                            )}
                        </div>
                    )}
                </div>
            </Fade>
        </Modal>
    );
};

export default PureDialog;

const modalContainerStyle = css`
    display: flex;
    align-items: center;
    justify-content: center;
`;

const containerStyle = css`
    min-width: 320px;
    max-width: 320px;
    padding: 16px;
    background-color: ${white.color};
    box-shadow: ${dp03.boxShadow};
    border-radius: 24px;
`;

const iconWrapperStyle = customStyle => css`
    display: flex;
    align-items: center;
    justify-content: center;

    & > * {
        font-size: 24px;
        color: ${success.color};
        text-align: center;
        margin-bottom: 4px;
        ${customStyle}
    }
`;

// adjustWidth: boolean (Dialog 寬度要扣掉 close icon 的寬度)
const titleWrapperStyle = (align = 'left', adjustWidth) => css`
    & > * {
        width: ${adjustWidth ? 'calc(100% - 24px)' : '100%'};
        font-weight: 500;
        line-height: 30px;
        letter-spacing: 0.15px;
        text-align: ${align};
    }
`;

const contentTextStyle = (align = 'left') => css`
    white-space: pre-line;
    font-weight: 500;
    line-height: 20px;
    letter-spacing: 0.1px;
    text-align: ${align};
`;

const closeIconWrapperStyle = css`
    position: absolute;
    top: 0;
    right: 0;
`;

const headerStyle = css`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const headerInfoStyle = css`
    flex: 1;
`;

const contentStyle = hasHeader => css`
    ${hasHeader && 'margin-top: 16px;'}
`;

const footerStyle = css`
    gap: 4px;
    display: flex;
    flex-direction: column;
    margin-top: 16px;
`;

const closeIconStyle = css`
    color: ${system900.color};
`;

const baseButtonStyle = css`
    min-height: 48px;
    padding: 14px 16px 14px 16px;
    gap: 8px;
    border-radius: 16px;

    & .MuiTypography-root {
        font-size: 14px;
        font-weight: 500;
        line-height: 20px;
        letter-spacing: 1.25px;
        text-align: center;
    }
`;

const primaryButtonStyle = css`
    ${baseButtonStyle}
`;

const secondaryButtonStyle = css`
    ${baseButtonStyle}
`;

PureDialog.propTypes = {
    children: childrenPropType,
    open: PropTypes.bool,
    isShowHeaderIcon: PropTypes.bool,
    isShowCloseIco: PropTypes.bool,
    isShowPrimaryButton: PropTypes.bool,
    isShowSecondaryButton: PropTypes.bool,
    isLoading: PropTypes.bool,
    titleAlign: PropTypes.oneOf(['left', 'center', 'right']),
    contentAlgin: PropTypes.oneOf(['left', 'center', 'right']),
    titleText: PropTypes.string,
    contentText: PropTypes.string,
    primaryButtonText: PropTypes.string,
    secondaryButtonText: PropTypes.string,
    iconStyle: stylePropType,
    buttonStyle: stylePropType,
    modalStyle: stylePropType,
    dialogContentStyle: stylePropType,
    Icon: PropTypes.object,
    onPrimaryButtonClick: PropTypes.func,
    onSecondaryButtonClick: PropTypes.func,
    onClose: PropTypes.func
};
